import { action, makeAutoObservable, runInAction } from "mobx";
import { AdminData, IAuthStore, Store } from "../global/types";
import axios from "axios";
import { changePassword, editUserProfile, getUserProfile, logIn, logOut } from "../api/user.action";

class AuthStore implements IAuthStore {
    rootStore: Store;
    authToken: string = "";
    isAuthenticated: boolean = false;
    loading: boolean = false;
    adminData: AdminData | null = null;

    constructor(rootStore: Store) {
        makeAutoObservable(this);
        this.rootStore = rootStore;
        this.initializeFromLocalStorage();
        axios.defaults.headers.common["source"] = "CMS";
    }
    
    initializeFromLocalStorage = () => {
        const token = localStorage.getItem("token"); 
        if (token) {
            this.setAuthToken(token);
        }
    }

    @action
    setAuthToken = (token: string | null) => {
        if (!token) {
            this.authToken = "";
            this.isAuthenticated = false;
            delete axios.defaults.headers.common["Authorization"];
            localStorage.removeItem("token");
            return;
        }
        this.authToken = token;
        this.isAuthenticated = true;
        axios.defaults.headers.common["Authorization"] = `${token}`;
        localStorage.setItem("token", token);
    }

    setLoading = (loading: boolean) => {
        this.loading = loading;
    }

    login = async (email: string, password: string) => {
        this.setLoading(true);
        try {
            const response = await logIn(email, password);
            const { token } = response.data;
            runInAction(() => {
                this.authToken = token
                this.setAuthToken(token);
            });
        } catch (error) {
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    logout = async () => {
        this.setLoading(true);
        try {
            await logOut()
            runInAction(() => {
                this.setAuthToken(null);
                this.rootStore.dashboardStore.resetDashboardData()
            });
        } catch (error: unknown) {
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    handleUnauthorized = (error: unknown) => {
        if (axios.isAxiosError(error) && error?.response?.status === 401) {
            this.setAuthToken(null);
        }
        else {
            throw error;
        }
    }

    requestChangePassword = async (oldPassword: string, newPassword: string) => {
        this.setLoading(true);
        try {
            const requestData = {
                old_password: oldPassword,
                new_password: newPassword
            }
            await changePassword(requestData);
        } catch (error: unknown) {
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    fetchUserProfile = async () => {
        this.setLoading(true);
        try {
            const response = await getUserProfile();
            runInAction(() => {
                this.adminData = response.data?.data;
            })
        } catch (error: unknown) {
            this.handleUnauthorized(error);
        } finally {
            this.setLoading(false);
        }
    }

    updateUserProfile = async (name: string) => {
        this.setLoading(true);
        try {
            const response = await editUserProfile(name);
            runInAction(() => {
                this.adminData = response.data?.data;
            })
        } catch (error: unknown) {
            this.handleUnauthorized(error);
        } finally {
            this.setLoading(false);
        }
    }

 }

export default AuthStore;
